/**
 * ReportsPage — Report selector and routing
 *
 * Displays a 2x2 grid of printable report types. When a report is selected,
 * renders the corresponding sub-component with back + print buttons.
 * Print button triggers window.print() which uses @media print CSS to hide
 * app chrome and show print-optimized layouts.
 *
 * Phase 9A: Printable reports for analog-friendly household members.
 */
import React, { useState } from 'react';
import WeeklySummaryReport from '../components/reports/WeeklySummaryReport';
import MonthlyBillCalendar from '../components/reports/MonthlyBillCalendar';
import MonthlyBudgetReport from '../components/reports/MonthlyBudgetReport';
import PayPeriodSnapshot from '../components/reports/PayPeriodSnapshot';
import AnnualReviewReport from '../components/reports/AnnualReviewReport';

const REPORT_TYPES = [
  {
    key: 'weekly',
    icon: '\u{1F4CB}',
    title: 'Weekly Summary',
    desc: 'Spendable, bills this week, budget status',
  },
  {
    key: 'calendar',
    icon: '\u{1F4C5}',
    title: 'Bill Calendar',
    desc: 'Monthly calendar with all bills',
  },
  {
    key: 'budget',
    icon: '\u{1F4CA}',
    title: 'Budget Report',
    desc: '50/30/20 breakdown by category',
  },
  {
    key: 'payperiod',
    icon: '\u{1F4B0}',
    title: 'Pay Period',
    desc: 'Current period bills & spending',
  },
  {
    key: 'annual',
    icon: '\u{1F4CA}',
    title: 'Year in Review',
    desc: 'Annual income, spending & savings summary',
  },
];

export default function ReportsPage() {
  const [reportType, setReportType] = useState(null);

  const renderReport = () => {
    switch (reportType) {
      case 'weekly': return <WeeklySummaryReport />;
      case 'calendar': return <MonthlyBillCalendar />;
      case 'budget': return <MonthlyBudgetReport />;
      case 'payperiod': return <PayPeriodSnapshot />;
      case 'annual': return <AnnualReviewReport />;
      default: return null;
    }
  };

  // Report selector grid
  if (!reportType) {
    return (
      <div>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>
          Reports
        </h2>
        <div className="report-selector">
          {REPORT_TYPES.map(rt => (
            <button
              key={rt.key}
              className="report-selector-card"
              onClick={() => setReportType(rt.key)}
            >
              <div className="report-selector-icon">{rt.icon}</div>
              <div className="report-selector-title">{rt.title}</div>
              <div className="report-selector-desc">{rt.desc}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Report view with back + print
  return (
    <div>
      <button className="report-back-btn" onClick={() => setReportType(null)}>
        &#8249; Back to Reports
      </button>
      {renderReport()}
      <button className="report-print-btn" onClick={() => window.print()}>
        Print Report
      </button>
    </div>
  );
}
